import React, { createContext, useCallback, useContext, useState } from 'react';

export type ComplementItem = {
  id: number | string;
  name?: string;
  order: number;
  rawMaterialId?: number;
  maximumQuantity: number;
  minimumQuantity: number;
  image?: File | string;
  price: number;
  pauseSale?: boolean;
  checked?: boolean;
  updateAll?: boolean;
  code?: string;
};

export type Complement = {
  id?: number | string;
  name?: string;
  order?: number;
  maximumQuantity?: number;
  minimumQuantity?: number;
  isRequired?: boolean;
  items?: ComplementItem[];
  pauseSale?: boolean;
};

type ComplementsContextData = Complement & {
  currentTab: 0 | 1;
  isDrawerOpened?: boolean;
  handleOpenDrawer(): void;
  handleCloseDrawer(): void;
  handleResetComplement(): void;
  handleAddComplementItem(): void;
  handleChangeTab(tab: 0 | 1): void;
  handleRemoveComplementItem(id: string | number): void;
  handleChangeComplementItem(complementItem: Partial<ComplementItem>): void;
  handleChangeComplement(complement: Partial<Complement>): void;
};

const ComplementsContext = createContext({} as ComplementsContextData);

const ComplementsProvider: React.FC = ({ children }) => {
  const [complement, setComplement] = useState<Complement>();
  const [currentTab, setCurrentTab] = useState<0 | 1>(0);
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>();

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpened(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpened(false);
  }, []);

  const handleChangeComplement = useCallback(
    (partialComplement: Partial<Complement>) => {
      setComplement((oldState) => ({ ...oldState, ...partialComplement }));
    },
    [],
  );

  const handleAddComplementItem = useCallback(() => {
    setComplement((oldState) => {
      const oldItems = oldState?.items || [];
      const lastItemOrder = oldItems[oldItems.length - 1]?.order || 0;

      return {
        ...oldState,
        items: [
          ...oldItems,
          {
            id: String(Math.random()),
            name: '',
            order: lastItemOrder + 1,
            rawMaterialId: undefined,
            maximumQuantity: 1,
            minimumQuantity: 0,
            image: undefined,
            price: 0,
            pauseSale: false,
            checked: false,
          },
        ],
      };
    });
  }, []);

  const handleRemoveComplementItem = useCallback((id: string | number) => {
    setComplement((oldState) => {
      const oldItems = oldState?.items || [];

      return {
        ...oldState,
        items: oldItems.filter((item) => item.id !== id),
      };
    });
  }, []);

  const handleChangeComplementItem = useCallback(
    (complementItem: Partial<ComplementItem>) => {
      setComplement((oldState) => {
        const oldItems = oldState?.items || [];

        return {
          ...oldState,
          items: oldItems.map((oldItem) => {
            if (oldItem.id === complementItem.id) {
              return {
                ...oldItem,
                ...complementItem,
              };
            }

            return oldItem;
          }),
        };
      });
    },
    [],
  );

  const handleChangeTab = useCallback((tab: 0 | 1) => {
    setCurrentTab(tab);
  }, []);

  const handleResetComplement = useCallback(() => {
    setComplement(undefined);
  }, []);

  return (
    <ComplementsContext.Provider
      value={{
        currentTab,
        isDrawerOpened,
        handleChangeTab,
        handleOpenDrawer,
        handleCloseDrawer,
        handleResetComplement,
        handleChangeComplement,
        handleAddComplementItem,
        handleChangeComplementItem,
        handleRemoveComplementItem,
        ...complement,
      }}
    >
      {children}
    </ComplementsContext.Provider>
  );
};

function useComplements(): ComplementsContextData {
  const context = useContext(ComplementsContext);

  if (!context)
    throw new Error('useComplements must be used within ComplementsContext');

  return context;
}

export { ComplementsProvider, useComplements };
